/**
 * @module ThemeStore
 * @description Alpine store powering the site's appearance switcher
 * (light / dark / system) with localStorage persistence and live
 * `prefers-color-scheme` syncing while "system" is selected.
 *
 * The `<html>` element gets a `.dark` class toggled on/off — Tailwind's
 * `dark:` variant is configured (see src/css/app.css) to key off that class
 * rather than only the OS media query, so this store is the single source
 * of truth for the active theme.
 *
 * A blocking inline script in the page `<head>` (see _layout.twig /
 * _layout-blank-fullscreen.twig) applies the stored preference before first
 * paint to avoid a flash of the wrong theme; this store keeps things in
 * sync afterwards and reacts to user changes + OS changes.
 */

const STORAGE_KEY = 'theme';
const THEMES = ['light', 'dark', 'system'];

function getStoredPreference() {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return THEMES.includes(stored) ? stored : 'system';
    } catch {
        return 'system';
    }
}

function systemPrefersDark() {
    return typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyTheme(preference) {
    const isDark = preference === 'dark' || (preference === 'system' && systemPrefersDark());
    document.documentElement.classList.toggle('dark', isDark);
}

/**
 * Registers Alpine.store('theme', ...). Call before Alpine.start().
 * @param {import('alpinejs').Alpine} Alpine
 */
export default function registerThemeStore(Alpine) {
    Alpine.store('theme', {
        preference: getStoredPreference(),

        init() {
            applyTheme(this.preference);

            if (typeof window.matchMedia === 'function') {
                const mql = window.matchMedia('(prefers-color-scheme: dark)');
                const onChange = () => {
                    if (this.preference === 'system') applyTheme('system');
                };
                if (mql.addEventListener) {
                    mql.addEventListener('change', onChange);
                } else if (mql.addListener) {
                    // Safari < 14 fallback
                    mql.addListener(onChange);
                }
            }
        },

        /**
         * @param {'light'|'dark'|'system'} preference
         */
        set(preference) {
            if (!THEMES.includes(preference)) return;
            this.preference = preference;
            try {
                window.localStorage.setItem(STORAGE_KEY, preference);
            } catch {
                // Storage unavailable (e.g. private browsing) — theme still applies for this page load.
            }
            applyTheme(preference);
        },

        isActive(preference) {
            return this.preference === preference;
        },
    });
}



